/**
 * Cron job configuration list — status cards, table, toggle and manual run.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '@/app/store';
import {
  fetchJobsThunk,
  selectAllJobs,
  selectJobsError,
  selectJobsLoading,
} from '@/app/store/jobsSlice';
import { JOB_NAMES, type Job, type JobName } from '@/app/types/jobs';
import { JobToggleModal } from '@/pages/Jobs/JobToggleModal';
import { JobRunNowModal } from '@/pages/Jobs/JobRunNowModal';
import { JobRunResultDrawer } from '@/pages/Jobs/JobRunResultDrawer';

import './JobsList.scss';

function formatJobLabel(name: JobName): string {
  return name
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatUpdatedAt(iso: string): string {
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${Math.max(0, sec)}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return date.toLocaleString();
}

function sortJobsByCatalog(jobs: Job[]): Job[] {
  const order = new Map<JobName, number>(JOB_NAMES.map((n, i) => [n, i]));
  return [...jobs].sort((a, b) => {
    const ai = order.get(a.job_name) ?? 99;
    const bi = order.get(b.job_name) ?? 99;
    return ai - bi;
  });
}

export const JobsList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const jobs = useSelector(selectAllJobs);
  const loading = useSelector(selectJobsLoading);
  const fetchError = useSelector(selectJobsError);

  const [toggleTarget, setToggleTarget] = useState<Job | null>(null);
  const [runTarget, setRunTarget] = useState<Job | null>(null);
  const [fetchCycle, setFetchCycle] = useState<'init' | 'done'>('init');

  useEffect(() => {
    void dispatch(fetchJobsThunk()).finally(() => setFetchCycle('done'));
  }, [dispatch]);

  const sortedJobs = useMemo(() => sortJobsByCatalog(jobs), [jobs]);

  const showFetchError = !loading && fetchError != null && fetchError.length > 0;
  const isInitialLoading = loading && jobs.length === 0;
  const showSummarySkeleton = loading;

  return (
    <div className="jobs-list">
      <header className="jobs-list__header">
        <h1>Jobs</h1>
        <p className="jobs-list__description">
          View schedules, enable or disable jobs, and trigger dry-runs or live runs (when enabled).
        </p>
      </header>

      <div className="jobs-list__summary" aria-busy={showSummarySkeleton}>
        {showSummarySkeleton
          ? JOB_NAMES.map((name) => (
              <div
                key={name}
                data-testid="job-summary-skeleton"
                className="jobs-list__summary-card jobs-list__summary-card--skeleton"
              >
                <span className="jobs-list__summary-skel-line jobs-list__summary-skel-line--title" />
                <span className="jobs-list__summary-skel-line jobs-list__summary-skel-line--pill" />
              </div>
            ))
          : sortedJobs.map((job) => (
              <div key={job.job_name} className="jobs-list__summary-card">
                <span className="jobs-list__summary-name">{formatJobLabel(job.job_name)}</span>
                <span
                  className={`jobs-list__pill ${job.enabled ? 'jobs-list__pill--enabled' : 'jobs-list__pill--disabled'}`}
                >
                  {job.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ))}
      </div>

      {showFetchError && (
        <div className="jobs-list--fallback jobs-list__error" role="alert">
          Error loading jobs: {fetchError}
        </div>
      )}

      {isInitialLoading ? (
        <div className="jobs-list--fallback jobs-list__loading">Loading jobs…</div>
      ) : fetchCycle === 'done' && !loading && fetchError == null && sortedJobs.length === 0 ? (
        <div className="jobs-list--fallback jobs-list__empty">No jobs returned from the API.</div>
      ) : (
        <div className="jobs-list__table-wrap">
          <table className="jobs-list__table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Schedule</th>
                <th>Status</th>
                <th>Last updated</th>
                <th>Updated by</th>
                <th>Disabled reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map((job) => (
                <tr key={job.job_name}>
                  <td className="jobs-list__cell-name">{formatJobLabel(job.job_name)}</td>
                  <td className="jobs-list__cell-mono">{job.schedule}</td>
                  <td>
                    <span
                      className={`jobs-list__pill ${job.enabled ? 'jobs-list__pill--enabled' : 'jobs-list__pill--disabled'}`}
                    >
                      {job.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>{formatUpdatedAt(job.updated_at)}</td>
                  <td className="jobs-list__cell-secondary">{job.updated_by ?? '—'}</td>
                  <td className="jobs-list__cell-reason">
                    {!job.enabled && job.disabled_reason != null && job.disabled_reason.length > 0
                      ? job.disabled_reason
                      : '—'}
                  </td>
                  <td className="jobs-list__actions">
                    <button
                      type="button"
                      className="btn-secondary jobs-list__action-btn"
                      onClick={() => setToggleTarget(job)}
                      aria-label={`Toggle ${job.job_name}`}
                    >
                      Toggle
                    </button>
                    <button
                      type="button"
                      className="btn-secondary jobs-list__action-btn"
                      onClick={() => setRunTarget(job)}
                      aria-label={`Run now ${job.job_name}`}
                    >
                      Run now
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <JobToggleModal
        job={toggleTarget}
        isOpen={toggleTarget != null}
        onClose={() => setToggleTarget(null)}
      />
      <JobRunNowModal
        job={runTarget}
        isOpen={runTarget != null}
        onClose={() => setRunTarget(null)}
      />
      <JobRunResultDrawer />
    </div>
  );
};

export default JobsList;
